import { Button as BaseButton } from "@base-ui/react/button";
import classNames from "classnames";
import * as React from "react";

import variants from "./buttonVariants.module.css";
import styles from "./RobotoButton.module.css";

/**
 * Visual style of the button. `primary` is the solid brand call-to-action;
 * `secondary` is the neutral outlined control that sits beside it.
 */
export type RobotoButtonVariant = "primary" | "secondary";

export interface RobotoButtonProps extends React.AriaAttributes {
  /** Visual style. */
  variant?: RobotoButtonVariant;
  /**
   * Icon rendered before the children. Accepts any React element (e.g. a
   * lucide or MUI icon). The variant sizes and colors it to match the label.
   */
  icon?: React.ReactNode;
  /** Shows a spinner after the label and disables interaction. */
  loading?: boolean;
  children?: React.ReactNode;
  /** Extra class(es) for call-site layout/positioning. */
  className?: string;
  disabled?: boolean;
  /**
   * @default "button"
   */
  type?: "button" | "submit" | "reset";
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  id?: string;
  title?: string;
}

const variantClass: Record<RobotoButtonVariant, string> = {
  primary: variants.primary,
  secondary: variants.secondary,
};

/**
 * A standard Button component.
 *
 * @todo Rename to Button in a follow up PR and deprecate old Button
 * component as part of moving off of MUI.
 *
 * @example
 * ```tsx
 * <RobotoButton variant="primary" icon={<PlusIcon />} onClick={handleClick}>
 *   New
 * </RobotoButton>
 * ```
 */
export const RobotoButton = React.forwardRef<
  HTMLButtonElement,
  RobotoButtonProps
>(function RobotoButton(
  {
    variant = "primary",
    icon,
    loading = false,
    disabled,
    type = "button",
    className,
    children,
    ...props
  },
  ref,
) {
  return (
    <BaseButton
      ref={ref}
      className={classNames(variantClass[variant], className)}
      disabled={disabled || loading}
      type={type}
      data-loading={loading || undefined}
      {...props}
    >
      {!loading && icon}
      {children}
      {loading && <span className={styles.spinner} aria-hidden="true" />}
    </BaseButton>
  );
});
